// Reports ▸ Advertising (CEO marketing plan; built 2026-10-21). Admin + finance.
// Spend, clicks, leads, orders, revenue and gross profit by channel and by
// campaign; last touch (default) or first touch; which spend sources are
// connected; and the conversion-upload queue so problems are visible.
// Rules + sums live in the API (services/ad-report.ts, ad-conversions.ts).
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { api, ApiError, apiErrorMessage } from "@/lib/api";
import { ReportShell, useReportRange } from "@/components/ReportShell";
import { Button } from "@/components/ui/Button";
import { cn } from "@/lib/utils";
import { useAuth } from "@/auth/AuthProvider";

type Model = "last" | "first";
interface Row {
  channel: string;
  campaign: string | null;
  spendCents: number;
  clicks: number;
  impressions: number;
  leads: number;
  orders: number;
  revenueCents: number;
  gpCents: number;
  gpPendingOrders: number;
  costPerOrderCents: number | null;
  payback: number | null;
  spendSource: "api" | "csv" | null;
}
interface PlatformSetup { connected: boolean; missing: string[] }
interface Setup {
  uploads: "on" | "off";
  spendImport: "on" | "off";
  google: PlatformSetup & { purchaseAction: boolean; leadAction: boolean };
  microsoft: PlatformSetup & { purchaseGoal: boolean; leadGoal: boolean };
}
type Report =
  | { ready: false; missing: string[]; setup: Setup; fetchedAt: string }
  | {
      ready: true;
      setup: Setup;
      byChannel: Row[];
      byCampaign: Row[];
      totals: Row;
      later: { orders: number; revenueCents: number; gpCents: number; gpPendingOrders: number };
      truncated: { shown: number; total: number } | null;
      uploads: {
        counts: Record<string, number>; waiting: number; lastError: { at: string; error: string; platform: string } | null; optOuts: number;
        paused: Partial<Record<"google" | "microsoft", { error: string; at: string }>>;
        retryable: number;
        waitingForCosts: Array<{ orderId: string; orderNumber: number | null; lines: string[] }>;
        dataFailures: Array<{ platform: string; kind: string; orderRef: string; orderNumber: number | null; error: string; at: string }>;
      };
      spendImported: Record<string, string>;
      fetchedAt: string;
    };

const CHANNEL: Record<string, string> = {
  google_search: "Google Search", google_shopping: "Google Shopping", microsoft: "Microsoft Ads", meta: "Meta", linkedin: "LinkedIn",
  tiktok: "TikTok", reddit: "Reddit", chatgpt: "ChatGPT (free)", chatgpt_ads: "ChatGPT Ads", ai_assistant: "Other AI assistants",
  email: "Email", referral: "Referral", organic_search: "Organic search", direct: "Direct", other: "Other",
};
/** Channels we pay for — they should have spend. */
const PAID = new Set(["google_search", "google_shopping", "microsoft", "meta", "linkedin", "tiktok", "reddit", "chatgpt_ads"]);

const usd = (cents: number | null, digits = 0) =>
  cents === null ? "—" : (cents / 100).toLocaleString(undefined, { style: "currency", currency: "USD", maximumFractionDigits: digits, minimumFractionDigits: digits });
const n = (v: number) => v.toLocaleString();

export function ReportAdvertisingPage() {
  const { preset, setPreset, range, setCustom } = useReportRange();
  const [model, setModel] = useState<Model>("last");
  const q = useQuery({
    queryKey: ["reports", "advertising", range.from, range.to, model],
    queryFn: () => api<Report>(`/reports/advertising?from=${range.from}&to=${range.to}&model=${model}`),
    retry: (count, err) => !(err instanceof ApiError && err.status === 403) && count < 2,
  });
  const data = q.data;

  return (
    <ReportShell
      wide
      title="Advertising"
      subtitle="What each channel and campaign cost, and the leads, orders and gross profit it brought. Gross profit is what the ad platforms are told an order was worth."
      preset={preset}
      onPresetChange={setPreset}
      range={range}
      onCustomRange={setCustom}
      fetchedAt={data?.fetchedAt}
    >
      <div className="mb-8 flex flex-wrap items-center gap-2">
        <span className="text-[11px] font-medium uppercase tracking-[0.12em] text-muted">Credit the</span>
        {(["last", "first"] as Model[]).map((m) => (
          <button
            key={m}
            type="button"
            onClick={() => setModel(m)}
            className={cn(
              "rounded-full px-4 py-1.5 text-sm font-semibold transition-colors",
              model === m ? "bg-brown-500 text-cream-50" : "bg-cream-100 text-brown-400 hover:bg-cream-200",
            )}
          >
            {m === "last" ? "Last touch" : "First touch"}
          </button>
        ))}
        <span className="ml-2 text-sm text-brown-400">
          {model === "last" ? "the last ad or site that brought them back" : "how they first found us"}
        </span>
      </div>

      {q.isLoading ? (
        <Panel><p className="py-10 text-center text-base text-brown-300">Loading…</p></Panel>
      ) : q.error instanceof ApiError && q.error.status === 403 ? (
        <Panel><p className="py-10 text-center text-base text-brown-400">Only admin, finance and marketing can see advertising numbers.</p></Panel>
      ) : q.isError || !data ? (
        <div className="rounded-lg border border-rust-300 bg-rust-50 p-10 text-center text-base text-rust-600">Couldn't load the report.</div>
      ) : !data.ready ? (
        <>
          <Panel>
            <h2 className="font-display text-2xl font-bold text-brown-500">Almost ready</h2>
            <p className="mt-2 text-base text-brown-400">
              One database step is waiting on Jake (scripts/push-ad-tracking.ts). Once it has run, this report fills in within a minute — no redeploy.
            </p>
            <p className="mt-3 text-sm text-brown-300">Missing: {data.missing.join(", ")}</p>
          </Panel>
          <SetupStrip setup={data.setup} imported={{}} />
        </>
      ) : (
        <Loaded data={data} />
      )}
    </ReportShell>
  );
}

function Loaded({ data }: { data: Extract<Report, { ready: true }> }) {
  const { user } = useAuth();
  const canUploadSpend = !!user?.roles?.some((r: string) => r === "admin" || r === "marketing");
  const t = data.totals;
  const l = data.later;
  return (
    <div className="space-y-10">
      {data.truncated && (
        <p className="rounded-md border border-rust-300 bg-rust-50 px-4 py-3 text-base text-rust-600">
          This range has {n(data.truncated.total)} paid orders — showing the first {n(data.truncated.shown)}. Pick a shorter range for complete numbers.
        </p>
      )}
      <div className="grid grid-cols-2 gap-4 md:grid-cols-4 xl:grid-cols-7">
        <Kpi label="Spend" value={usd(t.spendCents)} />
        <Kpi label="Leads" value={n(t.leads)} />
        <Kpi label="Orders" value={n(t.orders)} />
        <Kpi label="Revenue" value={usd(t.revenueCents)} sub="before tax" />
        <Kpi label="Gross profit" value={usd(t.gpCents)} sub={t.gpPendingOrders ? `${t.gpPendingOrders} order${t.gpPendingOrders === 1 ? "" : "s"} still waiting for costs` : undefined} />
        <Kpi label="Cost per order" value={usd(t.costPerOrderCents)} />
        <Kpi label="GP ÷ spend" value={t.payback === null ? "—" : `${t.payback.toFixed(2)}×`} sub="1.00× = the ads paid for themselves" />
      </div>

      <SetupStrip setup={data.setup} imported={data.spendImported} />

      <Section title="By channel">
        <RowsTable rows={data.byChannel} />
        {l.orders > 0 && (
          <p className="mt-3 text-base text-brown-400">
            <span className="font-semibold text-brown-500">Later orders from these clients</span> (placed more than 30 days after their visit — not credited to ads):{" "}
            {n(l.orders)} order{l.orders === 1 ? "" : "s"}, {usd(l.revenueCents)} revenue, {usd(l.gpCents)} gross profit
            {l.gpPendingOrders > 0 ? ` (+${l.gpPendingOrders} awaiting costs)` : ""}.
          </p>
        )}
      </Section>
      <Section title="By campaign" note="Campaign = the utm_campaign on the ad link, matched to the platform's campaign name or id.">
        <RowsTable rows={data.byCampaign} withCampaign />
      </Section>

      <Uploads uploads={data.uploads} />

      <div className="grid gap-6 xl:grid-cols-2">
        {/* Spend upload: admin + marketing; finance reads (CEO 2026-10-21). */}
        {canUploadSpend && <SpendCsv />}
        <OptOut count={data.uploads.optOuts} />
      </div>
    </div>
  );
}

function Panel({ children, className }: { children: React.ReactNode; className?: string }) {
  return <div className={cn("rounded-lg border border-brown-100 bg-white p-6", className)}>{children}</div>;
}
function Section({ title, note, children }: { title: string; note?: string; children: React.ReactNode }) {
  return (
    <section>
      <h2 className="font-display text-2xl font-bold text-brown-500">{title}</h2>
      {note && <p className="mt-1 text-sm text-brown-400">{note}</p>}
      <div className="mt-4">{children}</div>
    </section>
  );
}
function Kpi({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="rounded-lg border border-brown-100 bg-white p-5">
      <div className="text-[11px] font-medium uppercase tracking-[0.12em] text-muted">{label}</div>
      <div className="mt-2 font-display text-3xl font-semibold text-brown-500">{value}</div>
      {sub && <p className="mt-1 text-xs text-brown-400">{sub}</p>}
    </div>
  );
}

function RowsTable({ rows, withCampaign }: { rows: Row[]; withCampaign?: boolean }) {
  if (!rows.length) return <Panel><p className="py-6 text-center text-base text-brown-300">Nothing in this range yet.</p></Panel>;
  const th = "px-4 py-3 text-right text-[11px] font-medium uppercase tracking-[0.12em] text-muted";
  const td = "px-4 py-3 text-right tabular-nums";
  return (
    <div className="overflow-x-auto rounded-lg border border-brown-100 bg-white">
      <table className="w-full text-base text-ink">
        <thead className="border-b border-brown-100 bg-cream-50">
          <tr>
            <th className={cn(th, "text-left")}>Channel</th>
            {withCampaign && <th className={cn(th, "text-left")}>Campaign</th>}
            <th className={th}>Spend</th>
            <th className={th}>Clicks</th>
            <th className={th}>Leads</th>
            <th className={th}>Orders</th>
            <th className={th}>Revenue</th>
            <th className={th}>Gross profit</th>
            <th className={th}>Cost / order</th>
            <th className={th}>GP ÷ spend</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={`${r.channel}|${r.campaign ?? ""}`} className="border-b border-brown-50 last:border-0">
              <td className="px-4 py-3 font-semibold text-brown-500">{CHANNEL[r.channel] ?? r.channel}</td>
              {withCampaign && <td className="px-4 py-3 text-brown-500">{r.campaign}</td>}
              <td className={td}>
                {r.spendSource === null && PAID.has(r.channel) && r.spendCents === 0 ? (
                  <span className="text-sm text-brown-300">spend not connected</span>
                ) : (
                  <>
                    {usd(r.spendCents)}
                    {r.spendSource === "csv" && <span className="ml-1 text-xs text-brown-300">CSV</span>}
                  </>
                )}
              </td>
              <td className={td}>{r.clicks ? n(r.clicks) : "—"}</td>
              <td className={td}>{n(r.leads)}</td>
              <td className={td}>{n(r.orders)}</td>
              <td className={td}>{usd(r.revenueCents)}</td>
              <td className={td}>
                {usd(r.gpCents)}
                {r.gpPendingOrders > 0 && <span className="block text-xs text-brown-300">+{r.gpPendingOrders} awaiting costs</span>}
              </td>
              <td className={td}>{usd(r.costPerOrderCents)}</td>
              <td className={cn(td, "font-semibold", r.payback !== null && r.payback < 1 ? "text-rust-600" : "text-brown-500")}>
                {r.payback === null ? "—" : `${r.payback.toFixed(2)}×`}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function SetupStrip({ setup, imported }: { setup: Setup; imported: Record<string, string> }) {
  const when = (p: string) => (imported[p] ? `spend updated ${new Date(imported[p]!).toLocaleDateString()}` : null);
  const items: Array<{ name: string; state: string; ok: boolean; detail: string | null }> = [
    {
      name: "Google Ads",
      ok: setup.google.connected,
      state: setup.google.connected ? "Connected" : "Not connected",
      detail: setup.google.connected
        ? [!setup.google.purchaseAction && "purchase action not set", !setup.google.leadAction && "lead action not set", when("google")].filter(Boolean).join(" · ") || null
        : `${setup.google.missing.length} setting${setup.google.missing.length === 1 ? "" : "s"} missing`,
    },
    {
      name: "Microsoft Ads",
      ok: setup.microsoft.connected,
      state: setup.microsoft.connected ? "Connected" : "Not connected",
      detail: setup.microsoft.connected
        ? [!setup.microsoft.purchaseGoal && "purchase goal not set", !setup.microsoft.leadGoal && "lead goal not set", when("microsoft")].filter(Boolean).join(" · ") || null
        : `${setup.microsoft.missing.length} setting${setup.microsoft.missing.length === 1 ? "" : "s"} missing`,
    },
    { name: "LinkedIn · ChatGPT · Meta", ok: false, state: "Not connected", detail: "add their spend by CSV below" },
    { name: "Sending conversions", ok: setup.uploads === "on", state: setup.uploads === "on" ? "On" : "Off", detail: setup.uploads === "on" ? null : "queued, nothing sent until Jake's go" },
    { name: "Daily spend import", ok: setup.spendImport === "on", state: setup.spendImport === "on" ? "On" : "Off", detail: null },
  ];
  return (
    <div className="grid gap-3 md:grid-cols-3 xl:grid-cols-5">
      {items.map((i) => (
        <div key={i.name} className="rounded-lg border border-brown-100 bg-white p-4">
          <div className="text-[11px] font-medium uppercase tracking-[0.12em] text-muted">{i.name}</div>
          <div className={cn("mt-1 text-lg font-semibold", i.ok ? "text-brown-500" : "text-brown-300")}>{i.state}</div>
          {i.detail && <p className="mt-0.5 text-xs text-brown-400">{i.detail}</p>}
        </div>
      ))}
    </div>
  );
}

function Uploads({ uploads }: { uploads: Extract<Report, { ready: true }>["uploads"] }) {
  const qc = useQueryClient();
  const retry = useMutation({
    mutationFn: () => api<{ requeued: number; unpaused: number }>("/reports/advertising/retry", { method: "POST", body: JSON.stringify({}) }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["reports", "advertising"] }),
  });
  const c = uploads.counts;
  const paused = Object.entries(uploads.paused) as Array<["google" | "microsoft", { error: string; at: string }]>;
  const tiles: Array<[string, number, string]> = [
    ["Queued", Math.max(0, (c.pending ?? 0) - uploads.waiting), "waiting for the next send"],
    ["Waiting", uploads.waiting, "for costs, bills or a setting"],
    ["Sent", c.sent ?? 0, "to Google / Microsoft"],
    ["Failed", c.failed ?? 0, "gave up after retries"],
    ["Not sent", c.skipped ?? 0, "opted out, no ad consent, click too old, or cancelled"],
  ];
  return (
    <Section title="Conversion uploads" note="Each paid order and website request that came from an ad click, sent once to the platform it came from.">
      <div className="grid grid-cols-2 gap-4 md:grid-cols-5">
        {tiles.map(([label, value, sub]) => (
          <div key={label} className={cn("rounded-lg border bg-white p-5", label === "Failed" && value > 0 ? "border-rust-300" : "border-brown-100")}>
            <div className="text-[11px] font-medium uppercase tracking-[0.12em] text-muted">{label}</div>
            <div className="mt-2 font-display text-3xl font-semibold text-brown-500">{n(value)}</div>
            <p className="mt-1 text-xs text-brown-400">{sub}</p>
          </div>
        ))}
      </div>
      {paused.map(([p, info]) => (
        <p key={p} className="mt-4 rounded-md border border-rust-300 bg-rust-50 px-4 py-3 text-base text-rust-600">
          <span className="font-semibold">{p === "google" ? "Google Ads" : "Microsoft Ads"} is paused</span> — a setting is wrong, so nothing is being sent there
          (everything stays queued). Since {new Date(info.at).toLocaleString()}: {info.error}. Fix the Railway setting (it resumes by itself; it also tries again every 4 hours), or press Retry.
        </p>
      ))}
      {(uploads.retryable > 0 || paused.length > 0) && (
        <div className="mt-4 flex items-center gap-3">
          <Button variant="outline" onClick={() => retry.mutate()} disabled={retry.isPending}>{retry.isPending ? "Requeuing…" : `Retry failed${uploads.retryable ? ` (${n(uploads.retryable)})` : ""}`}</Button>
          <span className="text-sm text-brown-400">Retries uploads that failed for a setting or a temporary problem.</span>
          {retry.isSuccess && <span className="text-sm text-brown-500">Requeued {retry.data.requeued}{retry.data.unpaused ? `, resumed ${retry.data.unpaused} platform${retry.data.unpaused === 1 ? "" : "s"}` : ""}.</span>}
          {retry.isError && <span className="text-sm text-rust-600">{apiErrorMessage(retry.error, "Couldn't requeue.")}</span>}
        </div>
      )}
      {uploads.waitingForCosts.length > 0 && (
        <div className="mt-6">
          <h3 className="font-display text-xl font-bold text-brown-500">Waiting for costs</h3>
          <p className="mt-1 text-sm text-brown-400">These orders came from an ad, but a line has no cost yet, so its gross profit can't be sent. Enter the cost on the order.</p>
          <ul className="mt-3 divide-y divide-brown-50 rounded-lg border border-brown-100 bg-white">
            {uploads.waitingForCosts.map((w) => (
              <li key={w.orderId} className="flex flex-wrap items-baseline gap-x-4 gap-y-1 px-4 py-3 text-base">
                <Link to={`/orders/${w.orderId}`} className="font-semibold text-brown-500 underline-offset-2 hover:underline">
                  Order {w.orderNumber ?? w.orderId.slice(0, 8)}
                </Link>
                <span className="text-brown-400">no cost on: {w.lines.length ? w.lines.join(" · ") : "a line"}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      {uploads.dataFailures.length > 0 && (
        <div className="mt-6">
          <h3 className="font-display text-xl font-bold text-brown-500">Refused by the platform</h3>
          <p className="mt-1 text-sm text-brown-400">The ad platform won't take these (e.g. the click expired or isn't theirs). Retrying can't change that.</p>
          <ul className="mt-3 divide-y divide-brown-50 rounded-lg border border-brown-100 bg-white">
            {uploads.dataFailures.map((f) => (
              <li key={`${f.platform}|${f.kind}|${f.orderRef}`} className="px-4 py-3 text-base">
                <span className="font-semibold text-brown-500">{f.platform === "google" ? "Google" : "Microsoft"} · {f.kind}{f.orderNumber ? ` · order ${f.orderNumber}` : ""}</span>
                <span className="ml-2 text-sm text-brown-400">{new Date(f.at).toLocaleDateString()} — {f.error}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      {uploads.lastError && (
        <p className="mt-4 rounded-md border border-rust-300 bg-rust-50 px-4 py-3 text-sm text-rust-600">
          Last problem ({uploads.lastError.platform}, {new Date(uploads.lastError.at).toLocaleString()}): {uploads.lastError.error}
        </p>
      )}
    </Section>
  );
}

const INPUT = "w-full rounded-md border border-rule bg-white px-3 py-2 text-base text-ink";

function SpendCsv() {
  const qc = useQueryClient();
  const [csv, setCsv] = useState("");
  const [platform, setPlatform] = useState("linkedin");
  const m = useMutation({
    mutationFn: () => api<{ saved: number }>("/reports/advertising/spend-csv", { method: "POST", body: JSON.stringify({ csv, platform }) }),
    onSuccess: () => { setCsv(""); void qc.invalidateQueries({ queryKey: ["reports", "advertising"] }); },
  });
  return (
    <Panel>
      <h2 className="font-display text-xl font-bold text-brown-500">Add spend from a CSV</h2>
      <p className="mt-1 text-sm text-brown-400">
        For platforms not connected yet. Columns: <span className="font-mono">date, campaign, cost</span> (and <span className="font-mono">platform, clicks, impressions</span> if you have them). Re-uploading a day replaces it.
      </p>
      <div className="mt-4 flex flex-wrap items-center gap-3">
        <select aria-label="Platform" value={platform} onChange={(e) => setPlatform(e.target.value)} className="h-10 rounded-md border border-rule bg-white px-3 text-base">
          <option value="linkedin">LinkedIn</option>
          <option value="chatgpt">ChatGPT</option>
          <option value="meta">Meta</option>
          <option value="tiktok">TikTok</option>
          <option value="reddit">Reddit</option>
          <option value="other">Other</option>
        </select>
        <input
          type="file"
          accept=".csv,text/csv"
          aria-label="CSV file"
          className="text-sm"
          onChange={(e) => { const f = e.target.files?.[0]; if (f) void f.text().then(setCsv); }}
        />
      </div>
      <textarea aria-label="CSV text" value={csv} onChange={(e) => setCsv(e.target.value)} rows={5} className={cn(INPUT, "mt-3 font-mono text-sm")} placeholder={"date,campaign,cost,clicks\n2026-11-01,HR Leaders Q4,120.50,40"} />
      <div className="mt-3 flex items-center gap-3">
        <Button onClick={() => m.mutate()} disabled={!csv.trim() || m.isPending}>{m.isPending ? "Saving…" : "Save spend"}</Button>
        {m.isSuccess && <span className="text-sm text-brown-500">Saved {m.data.saved} day{m.data.saved === 1 ? "" : "s"}.</span>}
        {m.isError && <span className="text-sm text-rust-600">{apiErrorMessage(m.error, "Couldn't save.")}</span>}
      </div>
    </Panel>
  );
}

function OptOut({ count }: { count: number }) {
  const qc = useQueryClient();
  const [email, setEmail] = useState("");
  const m = useMutation({
    mutationFn: () => api<{ ok: true }>("/reports/advertising/opt-outs", { method: "POST", body: JSON.stringify({ email }) }),
    onSuccess: () => { setEmail(""); void qc.invalidateQueries({ queryKey: ["reports", "advertising"] }); },
  });
  return (
    <Panel>
      <h2 className="font-display text-xl font-bold text-brown-500">Privacy opt-outs</h2>
      <p className="mt-1 text-sm text-brown-400">
        Someone asked (e.g. to privacy@withother.com) not to be shared with ad platforms? Add their email — they are never uploaded again.
        Browsers sending Global Privacy Control are added automatically. {n(count)} on the list.
      </p>
      <form className="mt-4 flex flex-wrap gap-3" onSubmit={(e) => { e.preventDefault(); if (email.trim()) m.mutate(); }}>
        <input type="email" aria-label="Email to opt out" value={email} onChange={(e) => setEmail(e.target.value)} className={cn(INPUT, "max-w-sm")} placeholder="name@company.com" />
        <Button type="submit" disabled={!email.trim() || m.isPending}>Add opt-out</Button>
      </form>
      {m.isSuccess && <p className="mt-2 text-sm text-brown-500">Added.</p>}
      {m.isError && <p className="mt-2 text-sm text-rust-600">{apiErrorMessage(m.error, "Couldn't add.")}</p>}
    </Panel>
  );
}
